/**
 * Settings → Microsoft Purview (Enterprise): the organisation's sensitivity labels read from the
 * tenant as the Agent 365 blueprint, mapped onto the four sensitivities every workbench understands,
 * Purview's own verdict on prompts, and the SharePoint route for encrypting labels (docs/purview.md).
 */

import type { PurviewLabelView, PurviewSettings, PurviewTryAnswer } from '@aioe/shared';
import { Button } from '@heroui/react';
import { useState } from 'react';

import { problemText, routes } from '../api/client.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { ChoiceSelect, ErrorNote, inputClass, PlainPanel, StatusMark, Tick } from './ui.tsx';

type Sensitivity = PurviewLabelView['sensitivity'];
const SENSITIVITIES: Sensitivity[] = ['public', 'internal', 'confidential', 'restricted'];
const WORD: Record<Sensitivity, string> = { public: 'Public', internal: 'Internal', confidential: 'Confidential', restricted: 'Restricted' };
const EVALUATE: Record<NonNullable<PurviewSettings['purview']>['evaluate'], string> = { off: 'Off', prompts: 'Prompts to hosted models', all: 'Prompts and agent requests' };

export function PurviewPanel() {
  const view = useLoad(() => routes.purview(), []);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [siteUrl, setSiteUrl] = useState('');
  const [folder, setFolder] = useState('Mojo Up artifacts');
  const [newMatch, setNewMatch] = useState('');
  const [newAction, setNewAction] = useState<'allow' | 'ask' | 'block'>('ask');
  const [showPermissions, setShowPermissions] = useState(false);
  const act = async (f: () => Promise<unknown>) => {
    setError(undefined);
    setBusy(true);
    try {
      await f();
      view.reload();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  const v = view.data;
  const settings = v?.settings;
  const save = (patch: Partial<PurviewSettings>) => act(() => routes.connectPurview({ ...(settings ?? { rules: [] }), ...patch }));
  const active = (v?.labels ?? []).filter((l) => !l.removedAt && !l.tenantId);
  const partner = (v?.labels ?? []).filter((l) => l.tenantId);
  return (
    <PlainPanel title="Microsoft Purview">
      <ErrorNote error={error ?? view.error} />
      {v ? (
        <dl className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-y-1.5 text-sm sm:grid-cols-[10rem_minmax(0,1fr)]">
          <dt className="text-muted">Status</dt>
          <dd>{v.connected ? <StatusMark state="online" label="Connected" /> : <StatusMark state="idle" label="Not connected" />}</dd>
          {v.connected ? (
            <>
              <dt className="text-muted">Labels</dt>
              <dd className="tabular-nums">
                {active.length}
                {v.lastSyncAt ? <span className="text-muted"> · read {timeAgo(v.lastSyncAt)}</span> : null}
              </dd>
              <dt className="text-muted">Activity sent</dt>
              <dd className="tabular-nums">
                {v.activity.rowsSent}
                {v.activity.lastAt ? <span className="text-muted"> · last {timeAgo(v.activity.lastAt)}</span> : null}
              </dd>
              {v.lastSyncError || v.activity.lastError ? (
                <>
                  <dt className="text-muted">Last problem{v.activity.lastErrorAt && !v.lastSyncError ? <span className="block text-xs">{timeAgo(v.activity.lastErrorAt)}</span> : null}</dt>
                  <dd className="grid gap-1">
                    <span className="text-danger">{v.lastSyncError ?? v.activity.lastError}</span>
                    {!v.lastSyncError && (
                      <span className="flex flex-wrap items-center gap-2 text-xs text-muted">
                        It clears on the next activity sent.
                        <Button size="sm" variant="ghost" onPress={() => void routes.dismissPurviewProblem().then(() => view.reload())}>
                          Dismiss
                        </Button>
                      </span>
                    )}
                  </dd>
                </>
              ) : null}
            </>
          ) : null}
        </dl>
      ) : null}

      {v && !v.connected ? (
        <div className="mt-3 grid gap-3">
          <p className="text-sm text-muted">
            Your tenant’s sensitivity labels become the data-protection rule on every workbench: a label on a file decides whether its contents may reach a hosted model, pasted text that carries a label’s marking is caught, and Purview itself can be asked before a prompt leaves. The platform reads Purview as the Agent 365 blueprint, so connect that first.
          </p>
          {v.consentUrl ? (
            <p className="text-sm">
              A Global Administrator grants the permissions once, by assigning the Graph application roles to each application’s service principal (the commands are in the Purview guide). Microsoft refuses an agent blueprint some of them, so the platform calls as the blueprint where it may and as the AIOE API application{v.apiAppId ? ` (${v.apiAppId})` : ''} otherwise: grant the four Purview permissions to that application too. If the blueprint’s registration declares the permissions instead, the{' '}
              <a className="text-accent underline" href={v.consentUrl} target="_blank" rel="noreferrer">
                consent page
              </a>{' '}
              grants them; it answers AADSTS1003031 when the registration declares none.{' '}
              <button type="button" className="text-muted underline" onClick={() => setShowPermissions((x) => !x)}>
                {showPermissions ? 'Hide' : 'Which permissions?'}
              </button>
            </p>
          ) : (
            <p className="text-sm text-warning">Connect Agent 365 first: it is the first row of the Microsoft card in Integrations.</p>
          )}
          {showPermissions ? (
            <ul className="grid gap-1 text-xs text-muted">
              {v.permissions.map((p) => (
                <li key={p.name}>
                  <span className="mono text-foreground">{p.name}</span> <span className="text-foreground">on {p.app === 'api' ? 'the AIOE API application' : 'the blueprint and the AIOE API application'}</span> — {p.why}
                </li>
              ))}
            </ul>
          ) : null}
          <div>
            <Button size="sm" isPending={busy} isDisabled={!v.consentUrl} onPress={() => void act(() => routes.connectPurview())}>
              Connect
            </Button>
          </div>
        </div>
      ) : null}

      {v?.connected && settings ? (
        <div className="mt-4 grid gap-5">
          <section className="grid gap-2">
            <div className="flex items-center justify-between gap-2">
              <h4 className="text-sm font-medium">Labels</h4>
              <Button size="sm" variant="ghost" isPending={busy} onPress={() => void act(() => routes.syncPurview())}>
                Read again
              </Button>
            </div>
            <p className="text-xs text-muted">Each label’s sensitivity starts from its place in your tenant’s order; change one where your organisation sees it differently. Markings are the header, footer and watermark text the label stamps on content.</p>
            <ul className="divide-y divide-border/60 text-sm">
              {active.map((l) => (
                <LabelRow key={l.labelId} label={l} busy={busy} onMap={(s) => void act(() => routes.mapPurviewLabel(l.labelId, { sensitivity: s }))} />
              ))}
              {partner.length ? <li className="pt-2 text-xs text-muted">From partner tenants</li> : null}
              {partner.map((l) => (
                <LabelRow key={l.labelId} label={l} busy={busy} onMap={(s) => void act(() => routes.mapPurviewLabel(l.labelId, { sensitivity: s, name: l.name, tenantId: l.tenantId }))} onForget={() => void act(() => routes.forgetPurviewLabel(l.labelId))} />
              ))}
            </ul>
          </section>

          <section className="grid gap-2">
            <h4 className="text-sm font-medium">When a label is not one of yours</h4>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="grid gap-1 text-xs text-muted">
                A label this organisation does not know
                <ChoiceSelect label="A label this organisation does not know" className="w-full" value={settings.unknownLabel ?? 'confidential'} options={Object.fromEntries(SENSITIVITIES.map((s) => [s, `Treat as ${WORD[s]}`])) as Record<Sensitivity, string>} onChange={(v) => void save({ unknownLabel: v })} />
              </label>
              <label className="grid gap-1 text-xs text-muted">
                A label from another tenant
                <ChoiceSelect label="A label from another tenant" className="w-full" value={settings.foreignTenant ?? 'restricted'} options={Object.fromEntries(SENSITIVITIES.map((s) => [s, `Treat as ${WORD[s]}`])) as Record<Sensitivity, string>} onChange={(v) => void save({ foreignTenant: v })} />
              </label>
            </div>
          </section>

          <section className="grid gap-2">
            <h4 className="text-sm font-medium">Rules</h4>
            <p className="text-xs text-muted">A rule names a label (its name or id) or a sensitivity and says what a workbench does with content under it: allow it through, ask the person, or block it.</p>
            {settings.rules.length ? (
              <ul className="grid gap-1 text-sm">
                {settings.rules.map((r, i) => (
                  <li key={`${r.match}-${i}`} className="flex items-center justify-between gap-2">
                    <span>
                      <span className="mono">{r.match}</span> <span className="text-muted">→ {r.action}</span>
                    </span>
                    <Button size="sm" variant="ghost" isPending={busy} onPress={() => void save({ rules: settings.rules.filter((_, j) => j !== i) })}>
                      Remove
                    </Button>
                  </li>
                ))}
              </ul>
            ) : null}
            <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_9rem_auto]">
              <input className={`${inputClass} mono`} value={newMatch} onChange={(e) => setNewMatch(e.target.value)} placeholder="label:Highly Confidential  or  sensitivity:restricted" spellCheck={false} aria-label="What the rule matches" />
              <ChoiceSelect label="What happens" className="w-full" value={newAction} options={{ allow: 'Allow', ask: 'Ask the person', block: 'Block' } as Record<typeof newAction, string>} onChange={setNewAction} />
              <Button size="sm" variant="ghost" isPending={busy} isDisabled={!newMatch.trim()} onPress={() => void save({ rules: [...settings.rules, { match: newMatch.trim(), action: newAction }] }).then(() => setNewMatch(''))}>
                Add
              </Button>
            </div>
          </section>

          <section className="grid gap-2">
            <h4 className="text-sm font-medium">Ask Purview itself</h4>
            <p className="text-xs text-muted">Purview’s data loss prevention policies for AI apps judge the prompt before it goes to a hosted model, alongside the workbench’s own classifier. Needs a DLP policy with the blueprint as its location.</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="grid gap-1 text-xs text-muted">
                Evaluate
                <ChoiceSelect
                  label="Evaluate"
                  className="w-full"
                  value={(settings.purview?.evaluate ?? 'off') as keyof typeof EVALUATE}
                  options={EVALUATE}
                  onChange={(v) => void save({ purview: { evaluate: v as NonNullable<PurviewSettings['purview']>['evaluate'], classifier: settings.purview?.classifier ?? 'also', failClosed: settings.purview?.failClosed ?? false, budgetMs: settings.purview?.budgetMs ?? 4000 } })}
                />
              </label>
              <label className="grid gap-1 text-xs text-muted">
                The local model
                <ChoiceSelect
                  label="The local model"
                  className="w-full"
                  value={settings.purview?.classifier ?? 'also'}
                  isDisabled={!settings.purview || settings.purview.evaluate === 'off'}
                  options={{ also: 'Also run the local model after Purview', instead: 'Purview instead: no local model needed' }}
                  onChange={(classifier) => {
                    const base = settings.purview ?? { evaluate: 'prompts' as const, failClosed: false, budgetMs: 4000 };
                    // With no local model, Purview is the only judge of agents' own requests: it must see them too.
                    void save({ purview: { ...base, classifier, ...(classifier === 'instead' && base.evaluate === 'prompts' ? { evaluate: 'all' as const } : {}) } });
                  }}
                />
              </label>
              <div className="self-end text-sm">
                <Tick checked={settings.purview?.failClosed ?? false} isDisabled={!settings.purview || settings.purview.evaluate === 'off'} onChange={(v) => void save({ purview: { ...(settings.purview ?? { evaluate: 'prompts', classifier: 'also', budgetMs: 4000 }), failClosed: v } })}>
                  Stop the prompt when Purview cannot answer
                </Tick>
              </div>
            </div>
            {settings.purview?.classifier === 'instead' && settings.purview.evaluate === 'prompts' ? (
              <p className="text-xs text-warning">With Purview instead of a local model, agents’ own requests are checked only for labels and secret patterns. Choose “Prompts and agent requests” for full coverage.</p>
            ) : null}
            {settings.purview?.classifier === 'instead' ? (
              <p className="text-xs text-muted">Enterprise only: workbenches drop the local model only when this organisation serves the Purview clause. When Purview cannot answer, the workbench {settings.purview.failClosed ? 'stops the prompt' : 'falls back to secret and personal-data patterns'}.</p>
            ) : null}
            <TryPrompt />
          </section>

          <section className="grid gap-2">
            <h4 className="text-sm font-medium">Encrypting labels through SharePoint</h4>
            <p className="text-xs text-muted">A label that encrypts cannot be applied on a machine. With this on, a workbench sends the file here; the platform puts it in a SharePoint library, applies the label, and hands the protected file back. This runs as the AIOE API application{v.apiAppId ? ` (${v.apiAppId})` : ''}, not the blueprint: grant it Files.ReadWrite.All, or Sites.Selected with write on the site below. Microsoft meters the call.</p>
            {!v.apiCredential ? <p className="text-xs text-warning" title="AIOE_ENTRA_CLIENT_SECRET">The platform holds no client secret for its own Microsoft application yet, so this route cannot run. Whoever runs this deployment sets it.</p> : null}
            <div className="grid gap-3 sm:grid-cols-2">
              <Tick checked={settings.protectedLabels?.enabled ?? false} onChange={(v) => void save({ protectedLabels: { enabled: v, keepItems: settings.protectedLabels?.keepItems ?? false } })}>
                Offer the SharePoint route
              </Tick>
              <Tick checked={settings.protectedLabels?.keepItems ?? false} onChange={(v) => void save({ protectedLabels: { enabled: settings.protectedLabels?.enabled ?? false, keepItems: v } })}>
                Keep the labelled copy in SharePoint
              </Tick>
            </div>
            {v.location ? (
              <p className="text-sm">
                Files go to{' '}
                {v.location.webUrl ? (
                  <a className="text-accent underline" href={v.location.webUrl} target="_blank" rel="noreferrer">
                    {v.location.folderPath}
                  </a>
                ) : (
                  v.location.folderPath
                )}{' '}
                <span className="text-muted">on {new URL(v.location.siteUrl).pathname}</span>
              </p>
            ) : null}
            <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_12rem_auto]">
              <input className={`${inputClass} mono`} value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} placeholder="https://contoso.sharepoint.com/sites/Engineering" spellCheck={false} aria-label="SharePoint site" />
              <input className={inputClass} value={folder} onChange={(e) => setFolder(e.target.value)} placeholder="Folder" aria-label="Folder in the default library" />
              <Button size="sm" variant="ghost" isPending={busy} isDisabled={!siteUrl.trim()} onPress={() => void act(() => routes.setPurviewLocation({ siteUrl: siteUrl.trim(), folder: folder.trim() || 'Mojo Up artifacts' })).then(() => setSiteUrl(''))}>
                {v.location ? 'Move' : 'Set'}
              </Button>
            </div>
          </section>

          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="ghost" isPending={busy} onPress={() => void act(() => routes.disconnectPurview())}>
              Disconnect
            </Button>
            <span className="text-xs text-muted">Workbenches keep the last taxonomy they were given until the next policy refresh.</span>
          </div>
        </div>
      ) : null}
    </PlainPanel>
  );
}

function LabelRow({ label: l, busy, onMap, onForget }: { label: PurviewLabelView; busy: boolean; onMap: (s: Sensitivity) => void; onForget?: () => void }) {
  return (
    <li className="grid gap-1 py-2 sm:grid-cols-[minmax(0,1fr)_11rem_auto] sm:items-center sm:gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className={`truncate ${l.parentId ? 'pl-4' : ''}`}>{l.parentId ? l.name : l.fullName}</span>
          {l.hasProtection ? <StatusMark state="high" label="Encrypts" /> : null}
          {!l.isActive ? <StatusMark state="disabled" label="Inactive" /> : null}
        </div>
        {l.markings.length ? <p className="truncate text-xs text-muted">Marks: {l.markings.join(' · ')}</p> : l.tooltip ? <p className="truncate text-xs text-muted">{l.tooltip}</p> : null}
      </div>
      <ChoiceSelect
        label={`Sensitivity of ${l.fullName}`}
        value={l.sensitivity}
        isDisabled={busy}
        options={Object.fromEntries(SENSITIVITIES.map((s) => [s, `${WORD[s]}${!l.mapped && s === l.sensitivity ? ' (by order)' : ''}`])) as Record<Sensitivity, string>}
        onChange={onMap}
      />
      <div className="text-right">{onForget ? (
        <Button size="sm" variant="ghost" isPending={busy} onPress={onForget}>
          Forget
        </Button>
      ) : null}</div>
    </li>
  );
}

const ACTION_TEXT = { allow: 'Allowed', warn: 'Allowed with a warning', block: 'Blocked' } as const;

/** Ask Purview about a prompt as a workbench would, for the signed-in administrator. Nothing typed here is kept. */
function TryPrompt() {
  const [text, setText] = useState('');
  const [answer, setAnswer] = useState<PurviewTryAnswer>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const ask = async () => {
    setBusy(true);
    setError(undefined);
    try {
      setAnswer(await routes.tryPurview(text));
    } catch (e) {
      setAnswer(undefined);
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-1 grid gap-2 rounded-lg bg-surface-secondary p-3">
      <div className="text-sm font-medium">Try a prompt</div>
      <p className="text-xs text-muted">Purview is asked about you, as it would be for a prompt you send from a workbench. What you type is not kept.</p>
      <textarea className={`${inputClass} min-h-16`} value={text} onChange={(e) => setText(e.target.value)} placeholder="password=Hunter2!2026 and the AWS key AKIAIOSFODNN7EXAMPLE" aria-label="Prompt to try" />
      <div>
        <Button size="sm" variant="ghost" isPending={busy} isDisabled={!text.trim()} onPress={() => void ask()}>
          Ask Purview
        </Button>
      </div>
      <ErrorNote error={error} />
      {answer ? (
        <dl className="grid grid-cols-[9rem_minmax(0,1fr)] gap-y-1 text-sm">
          <dt className="text-muted">Your prompts</dt>
          <dd>
            {answer.inScope ? (
              <StatusMark state="online" label="In scope of a DLP policy" />
            ) : (
              <StatusMark state="idle" label="Not in scope yet" />
            )}
            {!answer.inScope ? <div className="text-xs text-muted">No policy names this app for you yet. A new policy takes up to an hour; check it names {answer.location ?? 'the blueprint'} and is enabled, and that pay-as-you-go is on.</div> : null}
          </dd>
          <dt className="text-muted">Purview’s verdict</dt>
          <dd>
            <StatusMark state={answer.verdict.action === 'block' ? 'error' : answer.verdict.action === 'warn' ? 'medium' : 'online'} label={answer.verdict.evaluated ? ACTION_TEXT[answer.verdict.action] : 'Not evaluated'} />
            {answer.verdict.reason ? <div className="text-xs text-muted">{answer.verdict.reason}</div> : null}
            {answer.verdict.policies?.length ? <div className="text-xs text-muted">Policy: {answer.verdict.policies.join(', ')}</div> : null}
          </dd>
          <dt className="text-muted">Asked as</dt>
          <dd className="text-xs">
            {answer.caller === 'api' ? 'The AIOE API application' : answer.caller === 'blueprint' ? 'The blueprint' : 'Not known'}
            {answer.location ? <span className="mono text-muted"> · location {answer.location}</span> : null}
          </dd>
        </dl>
      ) : null}
      {answer?.diagnostics ? (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted">What Purview answered</summary>
          <p className="mt-1 text-muted">Purview’s own reply, without your text. Empty actions with no errors mean no rule matched; errors mean Purview could not evaluate it.</p>
          <pre className="mono mt-1 max-h-64 overflow-auto rounded bg-surface p-2">{JSON.stringify(answer.diagnostics, null, 2)}</pre>
        </details>
      ) : null}
    </div>
  );
}
